"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isPhoneLayout = isPhoneLayout;
exports.installLayout = installLayout;
exports.useIsMobile = useIsMobile;
exports.usePortraitPhone = usePortraitPhone;
const react_1 = require("react");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 17 Oct 2026 · Is this a phone, and is it held upright?                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ONE DEFINITION OF A PHONE: A WINDOW UNDER 900px WIDE, OR A SCREEN WHOSE SHORT SIDE IS
 * UNDER 600px.** `isPhoneLayout` decides it; `installLayout` writes the answer on `<html>` as
 * `data-layout`, which base.css's `phone:` and `desktop:` variants read; `useIsMobile` asks the
 * same function. A phone on its side (~850 × 390) is still a phone; whether it is upright is a
 * second, separate question (`usePortraitPhone`), which is what the landscape-only views ask.
 *
 * ⚠️⚠️ 2 Nov 2026 · **THE SCREEN, NOT THE WINDOW'S HEIGHT — BECAUSE A KEYBOARD CHANGES THE ONE AND
 * NEVER THE OTHER.** This used to be a media query, *under 900 wide or no taller than 520*, the
 * height half for phones on their side. A tablet on its side is ~820 tall until its keyboard opens
 * and ~420 after, so tapping a search box made an iPad a phone: the toolbar swapped to its phone
 * form, the focused field left the page, the keyboard closed, the height came back and the desktop
 * returned — on every tap. Capping the height rule at 1024 wide fixed the iPad and left the small
 * Android tablets (~960 wide) in the loop. CSS cannot ask how big the device is (`device-height`
 * is deprecated), so the decision is made here, in script, and handed to CSS as an attribute.
 * 600 is Android's own phone/tablet line (`sw600dp`): phones are 360–480 on their short side,
 * the smallest tablets 600 and up.
 *
 * ⚠️ **A hook that FOLLOWS the viewport**, unlike `narrow.ts`'s one-shot read: a toolbar that
 * keeps its desktop layout after the window narrows is the bug this exists to prevent. `false`
 * where `matchMedia` does not exist (jsdom, server) and where the screen reports no size (jsdom
 * reports 0 × 0), so every test renders the desktop layout it was written against.
 */
const NARROW_QUERY = '(max-width: 899px)';
const PORTRAIT_QUERY = '(orientation: portrait)';
/** A screen whose short side is under this is a phone's, however the window is sized. */
const PHONE_SCREEN = 600;
/** The one decision — read by the hook, and by `installLayout` for CSS. */
function isPhoneLayout() {
    if (typeof window === 'undefined')
        return false;
    const narrow = typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches;
    const screen = window.screen;
    const short = Math.min(screen?.width ?? 0, screen?.height ?? 0);
    return narrow || (short > 0 && short < PHONE_SCREEN);
}
/**
 * ⚠️ Everything that can change the answer: the window's width crossing 900 (the media query's
 * own event), and a rotation or a resize — some browsers swap `screen.width` and `height` on
 * rotation and some do not, which `Math.min` makes harmless either way.
 */
function onLayoutChange(notify) {
    if (typeof window === 'undefined')
        return () => undefined;
    const list = typeof window.matchMedia === 'function' ? window.matchMedia(NARROW_QUERY) : null;
    list?.addEventListener('change', notify);
    window.addEventListener('resize', notify);
    window.addEventListener('orientationchange', notify);
    return () => {
        list?.removeEventListener('change', notify);
        window.removeEventListener('resize', notify);
        window.removeEventListener('orientationchange', notify);
    };
}
/**
 * ⚠️⚠️ **CALL BEFORE THE FIRST RENDER, IN EVERY APP THAT IMPORTS base.css.** Until it runs,
 * `<html>` carries no `data-layout`, and neither `phone:` nor `desktop:` applies — anything shown
 * only by one of them is hidden. Each `main.tsx` calls it above `createRoot`; `#root` is empty
 * until then, so there is no frame of the wrong layout.
 */
function installLayout(root = document.documentElement) {
    const write = () => {
        root.dataset.layout = isPhoneLayout() ? 'phone' : 'desktop';
    };
    write();
    return onLayoutChange(write);
}
function useMedia(query) {
    return (0, react_1.useSyncExternalStore)((notify) => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return () => undefined;
        }
        const list = window.matchMedia(query);
        list.addEventListener('change', notify);
        return () => {
            list.removeEventListener('change', notify);
        };
    }, () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(query).matches
        : false, () => false);
}
/** A phone, upright or on its side — base.css's `phone:`, decided by `isPhoneLayout`. */
function useIsMobile() {
    return (0, react_1.useSyncExternalStore)(onLayoutChange, isPhoneLayout, () => false);
}
/** A phone held upright — what the landscape-only views refuse. */
function usePortraitPhone() {
    const mobile = useIsMobile();
    const portrait = useMedia(PORTRAIT_QUERY);
    return mobile && portrait;
}
//# sourceMappingURL=viewport.js.map